/**
 * Canonical-URL rules for openbooq.com — THE single source of truth.
 *
 * THE CONVENTION: absolute, apex host, TRAILING SLASH.
 *     https://openbooq.com/how-it-works/      ✅ 200
 *     https://openbooq.com/how-it-works       ❌ 301 -> the line above
 *
 * WHY the trailing slash (this is forced on us, not a taste call):
 * `scripts/prerender.mjs` writes each prerendered route as a DIRECTORY —
 * build/<route>/index.html — and every static hub under public/ is a directory
 * too. nginx serves them via `try_files $uri $uri/`, so a bare path misses the
 * file, matches the directory, and nginx's index module emits its automatic
 * 301 to the slash form. The slash form is therefore the ONLY spelling of a
 * content page that returns 200. Non-prerendered SPA routes fall through to
 * /spa.html and answer 200 either way, so the slash form is safe everywhere —
 * which is what lets us have ONE rule for the whole site.
 *
 * This module is deliberately dependency-free ESM (.mjs) so it can be imported
 * by BOTH the Vite/React bundle (canonical <link> tags) and plain node build
 * scripts (sitemap generator, CI checker). One rule, one definition, so the
 * next prerender change cannot re-break canonicals and the sitemap separately.
 *
 * See docs/url-conventions.md.
 */

/** Apex origin. No trailing slash (it is the ORIGIN, not a URL). */
export const SITE_ORIGIN = 'https://openbooq.com';

/**
 * Normalize a site-relative path to the canonical spelling.
 *
 *   '/how-it-works'  -> '/how-it-works/'
 *   '/how-it-works/' -> '/how-it-works/'   (idempotent)
 *   '/'              -> '/'
 *   'how-it-works'   -> '/how-it-works/'   (leading slash enforced)
 *   '/sitemap.xml'   -> '/sitemap.xml'     (real files keep their extension)
 *
 * Query/hash are preserved and the slash goes BEFORE them, so
 * '/compare/quote?utm_source=x' -> '/compare/quote/?utm_source=x'.
 */
export function canonicalPath(path) {
  if (typeof path !== 'string' || path === '') return '/';

  // Split off ?query / #hash — the slash belongs on the path, not after them.
  const cut = path.search(/[?#]/);
  let pathname = cut === -1 ? path : path.slice(0, cut);
  const suffix = cut === -1 ? '' : path.slice(cut);

  if (!pathname.startsWith('/')) pathname = `/${pathname}`;

  // A last segment containing a dot is a real file (/sitemap.xml,
  // /roof-prices/data/index.json) — appending a slash would 404 it.
  const lastSegment = pathname.slice(pathname.lastIndexOf('/') + 1);
  const isFile = lastSegment.includes('.');

  if (!isFile && !pathname.endsWith('/')) pathname += '/';

  return pathname + suffix;
}

/**
 * Absolute canonical URL for a site-relative path.
 * '/how-it-works' -> 'https://openbooq.com/how-it-works/'
 *
 * Already-absolute openbooq.com URLs are re-normalized rather than doubled up,
 * so passing either spelling is safe.
 */
export function canonicalUrl(path) {
  if (typeof path === 'string' && path.startsWith(SITE_ORIGIN)) {
    return SITE_ORIGIN + canonicalPath(path.slice(SITE_ORIGIN.length) || '/');
  }
  return SITE_ORIGIN + canonicalPath(path);
}
